import type { EstadoVault } from "../hooks/useVault";
import type { RedKeel } from "../config/networks";
import { useActividad } from "../hooks/useActividad";
import { useGas } from "../hooks/useGas";
import { Hueco } from "./Modal";
import { num } from "../lib/formato";

/**
 * EL RESULTADO, SIN ADORNOS.
 *
 * El resumen enseña piezas sueltas —capital, ganancias, gas— y cada una por separado parece buena
 * noticia. La resta es lo que nadie hace y lo unico que contesta la pregunta de verdad: ¿esto me
 * esta dando dinero o no?
 *
 *     resultado = (capital hoy − lo que aportaste) + lo ganado − el gas gastado
 *
 * El primer parentesis es la perdida (o ganancia) por el movimiento del precio. Lo ganado son las
 * comisiones ya netas. El gas es lo que el robot se ha cobrado de tu bolsa por trabajar.
 */
export function Cuentas({
  v, red, ganadoTotal, capitalPct,
}: {
  v: EstadoVault;
  red: RedKeel;
  /** total ganado en comisiones, neto, en el token de referencia */
  ganadoTotal: number | null;
  /** capital para el %: el mayor entre el vivo y el de apertura */
  capitalPct?: number;
}) {
  const gas = useGas(v.direccion, red);
  const act = useActividad(v.direccion, red);

  const enRef = (a0: bigint, a1: bigint) => {
    const x0 = Number(a0) / 10 ** v.token0.decimals;
    const x1 = Number(a1) / 10 ** v.token1.decimals;
    return v.refEsToken0 ? x0 + (v.precio > 0 ? x1 / v.precio : 0) : x1 + x0 * v.precio;
  };

  const gastadoGas = gas.data ? enRef(gas.data.total0, gas.data.total1) : null;
  const porPrecio = v.diferencia; // capital hoy − objetivo
  const resultado =
    ganadoTotal != null && gastadoGas != null ? porPrecio + ganadoTotal - gastadoGas : null;

  // Rendimiento anualizado sobre el capital aportado. Es una aproximacion: usa `costBasisRef`, que
  // es el capital de HOY, no el medio del periodo — si has metido o sacado por el camino, el
  // porcentaje se desvia. Se dice, en vez de presentarlo como exacto.
  const anual =
    ganadoTotal != null && act.data && act.data.dias >= 3 && (capitalPct ?? v.objetivo) > 0
      ? (ganadoTotal / (capitalPct ?? v.objetivo)) * (365 / act.data.dias) * 100
      : null;

  const cargando = gas.isPending || act.isPending || ganadoTotal == null;

  return (
    <>
      <div className="cuentas">
        <header>
          <h3>El resultado, sin adornos</h3>
          <p className="detalle">
            Lo que has ganado con las comisiones, menos lo que ha costado, menos lo que se ha movido
            el precio.
          </p>
        </header>

        {cargando ? <Hueco alto={44} n={4} /> : (
          <div className="cuenta">
            <div className={`linea ${porPrecio < 0 ? "resta" : "suma"}`}>
              <span className="k">
                {porPrecio < 0 ? "Se ha movido el precio en tu contra" : "El precio se ha movido a tu favor"}
                <em>capital hoy {num(v.capitalHoy)} frente a los {num(v.objetivo)} que pusiste</em>
              </span>
              <span className="v">{porPrecio >= 0 ? "+" : ""}{num(porPrecio)}</span>
            </div>

            <div className="linea suma">
              <span className="k">
                Comisiones ganadas
                <em>ya sin el 10% de Keellabs{act.data && act.data.dias > 0 ? ` · en ${Math.round(act.data.dias)} día(s)` : ""}</em>
              </span>
              <span className="v">+{num(ganadoTotal!)}</span>
            </div>

            <div className="linea resta">
              <span className="k">
                Gas que se ha cobrado el robot
                <em>{gas.data?.veces ?? 0} cobros de tu bolsa · el desglose está abajo</em>
              </span>
              <span className="v">−{num(gastadoGas!)}</span>
            </div>

            <div className={`linea total ${resultado! < 0 ? "malo" : "bueno"}`}>
              <span className="k">Resultado hasta ahora</span>
              <span className="v">
                {resultado! >= 0 ? "+" : ""}{num(resultado!)} {v.ref.symbol}
              </span>
            </div>

            {anual != null && (
              <p className="detalle" style={{ margin: "4px 0 0" }}>
                Las comisiones solas van a un ritmo de <strong>{anual.toFixed(1)}% anual</strong>{" "}
                sobre el capital que pusiste. Es una estimación: usa el capital de hoy, así que si
                has metido o sacado por el camino se desvía.
              </p>
            )}
            {porPrecio < 0 && (
              <p className="detalle" style={{ margin: "4px 0 0" }}>
                Lo que se ha movido el precio <strong>no está perdido todavía</strong>: la
                recuperación abre el rango sesgado para devolvértelo si el precio vuelve. Solo se
                hace real si cierras.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="cuentas">
        <header>
          <h3>Qué ha hecho el robot</h3>
          <p className="detalle">
            No es solo cuántas veces: es si tu rango es el adecuado para este activo.
          </p>
        </header>

        {act.isPending ? <Hueco alto={44} n={3} /> : act.data && (
          <>
            <div className="tabla-gas">
              <div className="cab">
                <span>Operación</span><span className="n">Veces</span><span className="n">Cada</span>
              </div>
              <div className="fila">
                <span>Reajustes del rango</span>
                <span className="n dato">{act.data.reajustes}</span>
                <span className="n tenue">
                  {act.data.horasEntre ? `${act.data.horasEntre.toFixed(1)} h` : "—"}
                </span>
              </div>
              <div className="fila">
                <span>
                  De ellos, con recuperación
                  {act.data.topadas > 0 && <em> · {act.data.topadas} topada(s) por tu límite</em>}
                </span>
                <span className="n dato">{act.data.recuperaciones}</span>
                <span className="n tenue">
                  {act.data.reajustes > 0
                    ? `${Math.round((act.data.recuperaciones / act.data.reajustes) * 100)}%`
                    : "—"}
                </span>
              </div>
              <div className="fila">
                <span>Barridos de capital suelto</span>
                <span className="n dato">{act.data.barridos}</span>
                <span className="n tenue">
                  {act.data.dias > 0 ? `${(act.data.barridos / act.data.dias).toFixed(1)}/día` : "—"}
                </span>
              </div>
              {act.data.reinyecciones > 0 && (
                <div className="fila">
                  <span>Tramos de reserva soltados</span>
                  <span className="n dato">{act.data.reinyecciones}</span>
                  <span className="n tenue">—</span>
                </div>
              )}
            </div>

            <div className="lecturas">
              {act.data.horasEntre != null && act.data.horasEntre < 6 && (
                <p className="detalle malo">
                  Se reajusta cada {act.data.horasEntre.toFixed(1)} h de media: tu rango es estrecho
                  para lo que se mueve este activo. Cobra más comisiones, pero cada salida gasta gas.
                </p>
              )}
              {act.data.reajustes > 0 && act.data.recuperaciones / act.data.reajustes > 0.6 && (
                <p className="detalle">
                  La mayoría de los reajustes abrieron rango de recuperación: el precio lleva
                  cayendo y el robot está estirando el rango para devolverte el capital.
                </p>
              )}
              {act.data.topadas > 0 && (
                <p className="detalle malo">
                  {act.data.topadas} recuperación(es) se toparon con tu límite de estiramiento y no
                  llegaron a cubrir todo el capital. Se sube en Ajustes.
                </p>
              )}
              {act.data.reajustes === 0 && act.data.dias > 1 && (
                <p className="detalle">
                  Todavía no ha hecho falta ningún reajuste: el precio no se ha salido de tu rango.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
